import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { VERTICALS, GENERAL_STANDARDS } from "../data/content";
import { Reveal, SectionHead } from "./Reveal";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";
import ListenButton from "./ListenButton";
import ShareBadge from "./ShareBadge";
import { Dialog, DialogContent } from "./ui/dialog";
import { BookOpenCheck, ShieldCheck, PenLine, ChevronDown, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Verticals() {
  const { tr } = useI18n();
  const { user, signIn } = useAuth();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [showStandards, setShowStandards] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [form, setForm] = useState({ place: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const filtered = VERTICALS.filter((v) =>
    (v.name + v.desc + tr(v.name) + tr(v.desc)).toLowerCase().includes(q.toLowerCase())
  );

  const openBoard = (v) => { setOpen(v); setApplying(false); setApplied(false); setForm({ place: "", message: "" }); };

  const startApply = () => {
    if (!user) {
      toast.info(tr("Sign in with your SocialPulse identity to apply."));
      signIn();
      return;
    }
    setApplying(true);
  };

  const submitApplication = async (e) => {
    e.preventDefault();
    if (!form.place.trim()) return toast.error(tr("Please tell us which place you want to cover."));
    setSubmitting(true);
    try {
      await axios.post(`${API}/contributors`, { board: open.name, place: form.place, message: form.message },
        { headers: { Authorization: `Bearer ${localStorage.getItem("sp_token")}` } });
      setApplied(true);
      toast.success(tr("Application received — welcome aboard!"));
    } catch (_) {
      toast.error(tr("Something went wrong. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="verticals" data-testid="master-verticals-section" className="py-24 lg:py-32 bg-white">
      <SectionHead
        index="§ 03"
        eyebrow="Universal Taxonomy"
        title="Twenty community boards. Every place on Earth."
        sub="From a national capital to a Kerala panchayat — every location draws from the same horizontal information architecture. Tap any board to read its editorial instructions."
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-12">
        <Reveal>
          <div className="border border-[#BFD4F2] rounded-2xl bg-[#F6FAFF] overflow-hidden" data-testid="general-standards-banner">
            <div
              data-testid="general-standards-toggle"
              role="button"
              tabIndex={0}
              onClick={() => setShowStandards(!showStandards)}
              onKeyDown={(e) => e.key === "Enter" && setShowStandards(!showStandards)}
              className="w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-[#EAF2FD] transition-colors cursor-pointer"
            >
              <ShieldCheck size={20} className="text-[#2563EB] shrink-0" />
              <span className="font-bold text-[#0F2557] flex-1">{tr("General Publishing Standards — apply to all 20 boards")}</span>
              <span onClick={(e) => e.stopPropagation()}>
                <ListenButton compact text={`${tr("General Publishing Standards")}. ${GENERAL_STANDARDS.map(tr).join(" ")}`} />
              </span>
              <ChevronDown size={18} className={`text-[#2563EB] transition-transform duration-300 ${showStandards ? "rotate-180" : ""}`} />
            </div>
            {showStandards && (
              <div data-testid="general-standards-body" className="px-6 pb-6 pt-1 space-y-3 border-t border-[#DCE8F7]">
                {GENERAL_STANDARDS.map((p, i) => (
                  <p key={i} className="text-sm text-[#4A5568] leading-relaxed mt-3">{tr(p)}</p>
                ))}
              </div>
            )}
          </div>
        </Reveal>

        <Reveal>
          <input
            data-testid="vertical-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tr("Filter boards — try “food”, “history”, “jobs”…")}
            className="w-full md:w-96 bg-transparent border-b border-[#2563EB] font-mono-wp text-sm py-3 mt-10 placeholder:text-[#94A3B8] focus:outline-none focus:border-[#EA580C] transition-colors"
          />
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 mt-10 border-t border-l wp-rule">
          {filtered.map((v, i) => (
            <Reveal key={v.n} delay={(i % 4) * 0.05} y={16}>
              <button
                data-testid="vertical-card-item"
                onClick={() => openBoard(v)}
                className="w-full text-left border-r border-b wp-rule p-5 lg:p-6 h-full group hover:bg-[#EAF2FD] transition-colors duration-300"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono-wp text-[0.65rem] text-[#EA580C]">{v.n}</span>
                  <BookOpenCheck size={14} className="text-[#BFD4F2] group-hover:text-[#2563EB] transition-colors" />
                </div>
                <div className="font-atlas text-lg lg:text-xl text-[#0F2557] mt-2 leading-tight group-hover:text-[#2563EB] transition-colors duration-300">{tr(v.name)}</div>
                <div className="text-xs text-[#718096] mt-2 leading-relaxed">{tr(v.desc)}</div>
                <div className="text-[0.62rem] font-bold text-[#2563EB] mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">{tr("Editorial instructions")} →</div>
              </button>
            </Reveal>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full border-r border-b wp-rule p-10 text-center font-mono-wp text-xs text-[#718096]">{tr("No boards match")} “{q}”.</div>
          )}
        </div>
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent data-testid="vertical-detail-dialog" className="max-w-2xl bg-white rounded-3xl border-[#BFD4F2] p-0 overflow-hidden max-h-[85vh] overflow-y-auto">
          {open && (
            <div>
              <div className="px-8 pt-8 pb-6 bg-[#F6FAFF] border-b border-[#DCE8F7]">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono-wp text-xs text-[#EA580C]">{open.n} / 20 — {tr("Community Board")}</span>
                  <ListenButton compact text={`${tr(open.name)}. ${tr(open.publish)} ${tr(open.standards)}`} />
                </div>
                <h3 data-testid="vertical-dialog-title" className="font-atlas text-3xl text-[#0F2557] mt-2">{tr(open.name)}</h3>
                <p className="text-sm text-[#64748B] mt-1">{tr(open.desc)}</p>
              </div>
              <div className="px-8 py-7 space-y-7">
                <div>
                  <div className="flex items-center gap-2 text-[#2563EB] font-bold text-xs uppercase tracking-widest mb-3">
                    <PenLine size={14} /> {tr("What to publish")}
                  </div>
                  <p data-testid="vertical-dialog-publish" className="text-sm text-[#334155] leading-relaxed">{tr(open.publish)}</p>
                </div>
                <div className="border border-[#C7E8D2] bg-[#F0FDF4] rounded-2xl p-5">
                  <div className="flex items-center gap-2 text-[#15803D] font-bold text-xs uppercase tracking-widest mb-3">
                    <ShieldCheck size={14} /> {tr("Standards & sourcing")}
                  </div>
                  <p data-testid="vertical-dialog-standards" className="text-sm text-[#3F5548] leading-relaxed">{tr(open.standards)}</p>
                </div>
                <ShareBadge url={`https://worldpulse.pro/board/${open.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`} />

                {applied ? (
                  <div data-testid="contributor-success" className="border border-[#86EFAC] bg-[#F0FDF4] rounded-2xl p-5 text-center">
                    <div className="font-extrabold text-[#15803D]">{tr("Application received!")}</div>
                    <p className="text-xs text-[#3F5548] mt-1">{tr("Our territory team will review it and reach out by email.")}</p>
                  </div>
                ) : applying ? (
                  <form onSubmit={submitApplication} data-testid="contributor-form" className="border border-[#BFD4F2] bg-[#F6FAFF] rounded-2xl p-5 space-y-4">
                    <div className="text-xs font-extrabold uppercase tracking-widest text-[#1D4ED8]">{tr("Contributor application")} — {tr(open.name)}</div>
                    <input
                      data-testid="contributor-place-input"
                      value={form.place}
                      onChange={(e) => setForm({ ...form, place: e.target.value })}
                      placeholder={tr("Which place will you cover? e.g. Kottayam, Portland…")}
                      className="w-full bg-white border border-[#DCE8F7] rounded-xl px-4 py-3 text-sm text-[#0F2557] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#2563EB]"
                    />
                    <textarea
                      data-testid="contributor-message-input"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder={tr("Tell us about your local knowledge and experience (optional)")}
                      rows={3}
                      className="w-full bg-white border border-[#DCE8F7] rounded-xl px-4 py-3 text-sm text-[#0F2557] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#2563EB] resize-none"
                    />
                    <button
                      data-testid="contributor-submit-button"
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold text-sm py-3 rounded-xl transition-colors duration-200 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {submitting && <Loader2 size={14} className="animate-spin" />}
                      {tr("Submit application")} →
                    </button>
                  </form>
                ) : (
                  <button
                    data-testid="vertical-dialog-contribute"
                    onClick={startApply}
                    className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold text-sm py-3.5 rounded-xl transition-colors duration-200 active:scale-[0.98]"
                  >
                    {tr("Become a contributor for this board")} →
                  </button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
